import BillingModel from "../../../../types/billing-model";

export type BillingsTaxiDriverDetailsDriverUser = {
  firstName: string;
  lastName: string;
};

export type BillingsTaxiDriverDetailsDriverAddress = {
  displayName: string;
};

export type BillingsTaxiDriverDetailsDriver = {
  user: BillingsTaxiDriverDetailsDriverUser;
  startingAddress: BillingsTaxiDriverDetailsDriverAddress;
};

export type BillingsTaxiDriverDetailsFleetPartner = {
  name: string;
};

export type BillingsTaxiDriverDetailsTaxiDriverAssociation = {
  driver: BillingsTaxiDriverDetailsDriver;
  fleetPartner: BillingsTaxiDriverDetailsFleetPartner | null;
};

export type BillingsTaxiDriverDetailsContract = {
  model: BillingModel;
  distanceRate: number;
  haltingTimeRate: number;
  haltingTimeAfterMinutes: number;
  taxiDriverAssociation: BillingsTaxiDriverDetailsTaxiDriverAssociation;
  description: string;
};

export type BillingsTaxiDriverDetailsBillingNode = {
  id: string;
  nodeId: string;
  lat: number;
  lon: number;
  description: string;
  checkoutDate: Date | null;
  checkoutEventId: string | null;
  plannedDate: Date | null;
  orderId: string | null;
  distance: number;
  plannedDistance: number;
  haltingTime: number;
  highwayCharge: number;
  allowCharge: boolean;
  editableCoordinates: boolean;
};

export enum BillingsTaxiDriverDetailsContributionType {
  BASE_AMOUNT_EXTERNAL_TAXI = "BASE_AMOUNT_EXTERNAL_TAXI",
  BONUS_FAVORABLE_DISTANCE = "BONUS_FAVORABLE_DISTANCE",
  BONUS_OTHER = "BONUS_OTHER",
  PENALTY_WRONG_ROUTE = "PENALTY_WRONG_ROUTE",
  PENALTY_BEING_LATE = "PENALTY_BEING_LATE",
  PENALTY_INCOMPATIBLE_CAR = "PENALTY_INCOMPATIBLE_CAR",
  PENALTY_OTHER = "PENALTY_OTHER",
  CONTRACT_CORRECTION_RATE = "CONTRACT_CORRECTION_RATE",
}

export type BillingsTaxiDriverDetailsContributionContractCorrectionBaseAmountExternalTaxi =
  {
    type: BillingsTaxiDriverDetailsContributionType.BASE_AMOUNT_EXTERNAL_TAXI;
    comment: string | null;
    distance: number;
    amount: number;
  };

export type BillingsTaxiDriverDetailsContributionContractCorrectionRate = {
  type: BillingsTaxiDriverDetailsContributionType.CONTRACT_CORRECTION_RATE;
  comment: string | null;
  rate: number;
};

export type BillingsTaxiDriverDetailsContributionBonusFavorableDistance = {
  type: BillingsTaxiDriverDetailsContributionType.BONUS_FAVORABLE_DISTANCE;
  comment: string | null;
  distance: number;
  rate: number;
};

export type BillingsTaxiDriverDetailsContributionBonusOther = {
  type: BillingsTaxiDriverDetailsContributionType.BONUS_OTHER;
  comment: string | null;
  amount: number;
};

export type BillingsTaxiDriverDetailsContributionPenaltyWrongRoute = {
  type: BillingsTaxiDriverDetailsContributionType.PENALTY_WRONG_ROUTE;
  comment: string | null;
  amount: number;
};

export type BillingsTaxiDriverDetailsContributionPenaltyBeingLate = {
  type: BillingsTaxiDriverDetailsContributionType.PENALTY_BEING_LATE;
  comment: string | null;
  amount: number;
};

export type BillingsTaxiDriverDetailsContributionPenaltyIncompatibleCar = {
  type: BillingsTaxiDriverDetailsContributionType.PENALTY_INCOMPATIBLE_CAR;
  comment: string | null;
  amount: number;
};

export type BillingsTaxiDriverDetailsContributionPenaltyOther = {
  type: BillingsTaxiDriverDetailsContributionType.PENALTY_OTHER;
  comment: string | null;
  amount: number;
};

export type BillingsTaxiDriverDetailsContributionContractCorrection =
  | BillingsTaxiDriverDetailsContributionContractCorrectionBaseAmountExternalTaxi
  | BillingsTaxiDriverDetailsContributionContractCorrectionRate;

export type BillingsTaxiDriverDetailsContributionBonus =
  | BillingsTaxiDriverDetailsContributionBonusFavorableDistance
  | BillingsTaxiDriverDetailsContributionBonusOther;

export type BillingsTaxiDriverDetailsContributionPenalty =
  | BillingsTaxiDriverDetailsContributionPenaltyWrongRoute
  | BillingsTaxiDriverDetailsContributionPenaltyBeingLate
  | BillingsTaxiDriverDetailsContributionPenaltyIncompatibleCar
  | BillingsTaxiDriverDetailsContributionPenaltyOther;

export type BillingsTaxiDriverDetailsContribution =
  | BillingsTaxiDriverDetailsContributionBonus
  | BillingsTaxiDriverDetailsContributionPenalty
  | BillingsTaxiDriverDetailsContributionContractCorrection;

export type BillingsTaxiDriverDetailsCargoOrder = {
  id: string;
  humanId: number;
};

export type BillingsTaxiDriverDetailsTransportingOrder = {
  cargoOrder: BillingsTaxiDriverDetailsCargoOrder;
};

export type BillingsTaxiDriverDetailsSolvedOrder = {
  transportingOrder: BillingsTaxiDriverDetailsTransportingOrder;
};

export type BillingsTaxiDriverDetailsPlanEntry = {
  humanId: number;
  id: string;
  solvedOrders: BillingsTaxiDriverDetailsSolvedOrder[];
};

type BillingsTaxiDriverDetails = {
  contract: BillingsTaxiDriverDetailsContract;
  billingNodes: BillingsTaxiDriverDetailsBillingNode[];
  billingContributions: BillingsTaxiDriverDetailsContribution[];
  allContributionsAmount: number;
  amountForChargeHaltings: number;
  amountForChargeHighways: number;
  distance: number;
  amountForDistance: number;
  planEntry: BillingsTaxiDriverDetailsPlanEntry;
  date: Date;
  passengers: string[];
  rateForDistance: number;
  correctedContractModel: BillingModel;
  sumOfBonuses: number;
  sumOfPenalties: number;
};

export default BillingsTaxiDriverDetails;
